export const QR_CODE_STYLE = {
  fgColor: "#0e7490",
  bgColor: "#f8fafc",
  cardColor: "#ffffff",
  textColor: "#475569",
  accentColor: "#0891b2",
  borderColor: "rgba(8, 145, 178, 0.22)",
  shadowColor: "rgba(14, 116, 144, 0.14)",
  gradientStops: ["#f0fdfa", "#f8fafc", "#ecfeff"] as const,
  fontFamily: '"Segoe UI", "Helvetica Neue", system-ui, sans-serif',
  fontSize: 40,
  fontWeight: 500,
  lineHeight: 36,
  padding: 80,
  cardRadius: 20,
  textAreaHeight: 88,
} as const;

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function fillCanvasBackground(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, QR_CODE_STYLE.gradientStops[0]);
  gradient.addColorStop(0.5, QR_CODE_STYLE.gradientStops[1]);
  gradient.addColorStop(1, QR_CODE_STYLE.gradientStops[2]);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

function drawQrCard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
): void {
  ctx.save();
  ctx.shadowColor = QR_CODE_STYLE.shadowColor;
  ctx.shadowBlur = 28;
  ctx.shadowOffsetY = 10;
  ctx.fillStyle = QR_CODE_STYLE.cardColor;
  roundRect(ctx, x, y, width, height, QR_CODE_STYLE.cardRadius);
  ctx.fill();
  ctx.restore();

  ctx.strokeStyle = QR_CODE_STYLE.borderColor;
  ctx.lineWidth = 2;
  roundRect(ctx, x, y, width, height, QR_CODE_STYLE.cardRadius);
  ctx.stroke();
}

export function drawUrlTextOnCanvas(
  ctx: CanvasRenderingContext2D,
  url: string,
  canvasWidth: number,
  startY: number,
  padding: number
): void {
  const maxWidth = canvasWidth - padding * 2;
  const words = url.split("/");
  let line = "";
  let y = startY;

  ctx.fillStyle = QR_CODE_STYLE.textColor;
  ctx.font = `${QR_CODE_STYLE.fontWeight} ${QR_CODE_STYLE.fontSize}px ${QR_CODE_STYLE.fontFamily}`;
  ctx.textAlign = "center";

  for (const word of words) {
    const testLine = line + word + "/";
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth && line !== "") {
      ctx.fillText(line, canvasWidth / 2, y);
      line = word + "/";
      y += QR_CODE_STYLE.lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, canvasWidth / 2, y);
}

export function downloadQrCodePngFromSvg(
  svg: SVGElement,
  url: string,
  filename: string
): void {
  const svgData = new XMLSerializer().serializeToString(svg);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const img = new Image();

  img.onload = () => {
    const { padding, textAreaHeight } = QR_CODE_STYLE;
    const cardPadding = 28;
    const cardWidth = img.width + cardPadding * 2;
    const cardHeight = img.height + cardPadding * 2;

    canvas.width = cardWidth + padding * 2;
    canvas.height = cardHeight + padding * 2 + textAreaHeight;

    if (ctx) {
      fillCanvasBackground(ctx, canvas.width, canvas.height);

      const cardX = padding;
      const cardY = padding;
      drawQrCard(ctx, cardX, cardY, cardWidth, cardHeight);

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(
        img,
        cardX + cardPadding,
        cardY + cardPadding,
        img.width,
        img.height
      );

      drawUrlTextOnCanvas(
        ctx,
        url,
        canvas.width,
        cardY + cardHeight + 52,
        padding
      );
    }

    const downloadLink = document.createElement("a");
    downloadLink.download = filename;
    downloadLink.href = canvas.toDataURL("image/png");
    downloadLink.click();
  };

  img.src = "data:image/svg+xml;base64," + btoa(svgData);
}
